import { Address, hashTypedData, Hex, keccak256, toBytes, TypedDataDomain } from 'viem';
import { OrderType, PreCreateOrder, CreateOrder } from '@sdk/shared/types/order.types';
import { orderSigChain } from '@sdk/orderExecutor/helpers/order';
import { Network } from '@sdk/shared/types/config.types';

export interface PrepareOrderArgs {
  trader: Hex;
  merchant: Hex;
  token: Hex;
  currency: string;
  paymentMethod: string;
  quantity: number;
  orderType: OrderType;
  traderChain: number;
  merchantChain: number;
  duration: number;
  paymentConfig?: number;
  userAddress: `0x${string}`;
  activeChainId: number;
  activeChain?: { id: number; name: string };
  expiry: number;
  tokenDecimals: number;
}

interface PrepareOrderResult {
  madeOrder: CreateOrder;
  orderHash: Hex;
  orderArgs: PreCreateOrder;
  domain: TypedDataDomain;
  p2pContract: Hex;
  sigchainId: number;
}

export function makeOrder(preOrder: PreCreateOrder): CreateOrder {
  return {
    ...preOrder,
    traderChain: BigInt(preOrder.traderChain),
    merchantChain: BigInt(preOrder.merchantChain),
    currency: keccak256(toBytes(preOrder.currency)),
    paymentMethod: keccak256(toBytes(preOrder.paymentMethod)),
    quantity: BigInt(Math.floor(preOrder.quantity * 10 ** preOrder.tokenDecimals)),
    expiry: BigInt(preOrder.expiry),
    duration: BigInt(preOrder.duration),
  };
}

// Create EIP-712 domain
export const getIexchangeDomain = (chainId: number, verifyingContract: Hex) => ({
  name: 'IExchange P2P Protocol',
  version: 'v2',
  chainId,
  verifyingContract,
});

export async function getP2PContract(sigchainId: number, networks: Network[]): Promise<Address> {
  const p2p = networks?.find(n => n.id === sigchainId)?.contract;
  if (!p2p) {
    console.error('P2P contract not found', { sigchainId, networks });
    throw new Error('P2P contract not found');
  }
  return p2p;
}

/**
 * Prepare order parameters for signature and contract interaction
 */
export async function prepareOrder(
  args: PrepareOrderArgs,
  p2pContract: Address,
  sigchainId: number,
): Promise<PrepareOrderResult> {
  // Create the order arguments (PreCreateOrder)
  const orderArgs: PreCreateOrder = {
    trader: args.trader as `0x${string}`,
    merchant: args.merchant as `0x${string}`,
    token: args.token as `0x${string}`,
    currency: args.currency,
    paymentMethod: args.paymentMethod,
    quantity: args.quantity,
    orderType: args.orderType,
    traderChain: args.traderChain,
    merchantChain: args.merchantChain,
    duration: args.duration,
    expiry: args.expiry,
    tokenDecimals: args.tokenDecimals,
  };

  // Convert PreCreateOrder to CreateOrder
  const madeOrder = makeOrder(orderArgs);

  // Generate order hash
  const domain = getIexchangeDomain(sigchainId, p2pContract);
  const orderHash = generateOrderHash(madeOrder, domain);
  console.log('orderHash', orderHash, madeOrder, domain);

  return {
    madeOrder,
    orderHash,
    orderArgs,
    domain,
    p2pContract,
    sigchainId,
  };
}

export const IEX_TYPES = {
  CreateOrder: [
    { name: 'trader', type: 'address' },
    { name: 'merchant', type: 'address' },
    { name: 'traderChain', type: 'uint256' },
    { name: 'merchantChain', type: 'uint256' },
    { name: 'token', type: 'address' },
    { name: 'currency', type: 'bytes32' },
    { name: 'paymentMethod', type: 'bytes32' },
    { name: 'orderType', type: 'uint8' },
    { name: 'quantity', type: 'uint256' },
    { name: 'expiry', type: 'uint256' },
    { name: 'duration', type: 'uint256' },
  ],
} as const;

export const generateOrderHash = (order: CreateOrder, domain: TypedDataDomain) => {
  return hashTypedData({
    domain,
    types: IEX_TYPES,
    primaryType: 'CreateOrder',
    message: order,
  });
};
